import { PhoneStorageBreakdown } from './PhoneStorageBreakdown';
import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Smartphone, Wifi, WifiOff, AlertTriangle, CheckCircle } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import {
  canOfficeResolveSubmission,
  type MobileSubmissionState,
} from "@shared/mobile-submission-state";
import { describeOperatorDelivery, formatPhoneReportTime } from "@/pages/tracker/operatorDeliveryStatus";
import type { SyncFlightRecorderPayload } from "@shared/sync-flight-recorder";

export interface DeviceHealthSnapshot {
  id: string;
  operatorId: number;
  deviceId: string | null;
  installationId: string | null;
  osName: string | null;
  osVersion: string | null;
  appVersion: string | null;
  outboxCount: number | null;
  oldestEventSeconds: number | null;
  lastSyncAt: string | null;
  consecutiveFailures: number | null;
  dbSizeMb: number | null;
  storageDiagnostics?: unknown;
  offlineMediaBytes: number | null;
  offlineMediaFileCount: number | null;
  outboxSuccessRowCount: number | null;
  outboxFailedPermanentRowCount: number | null;
  lastLocalRetentionAt: string | null;
  lastRetentionMediaDeletedCount: number | null;
  lastRetentionOutboxRowsDeletedCount: number | null;
  storageTelemetryReadOk: boolean | null;
  pendingSignoffs: number | null;
  pendingTaskCompletions: number | null;
  unsafeStatusReadOk: boolean | null;
  unsafeCount: number | null;
  oldestUnsafeSeconds: number | null;
  unsafeCounts: UnsafeCounts | null;
  unsafeOldestSecondsByStatus: Partial<Record<keyof UnsafeCounts, number | null>> | null;
  unsafeByEventType: Record<string, { unsafeCount: number; oldestUnsafeSeconds: number | null }> | null;
  protectedEvidenceMissingStoreCount: number | null;
  oldestProtectedEvidenceMissingStoreSeconds: number | null;
  evidenceOperationInventory: EvidenceOperationInventoryEntry[] | null;
  evidenceOperationInventoryTruncated: boolean | null;
  orphanPhotoFileCount: number | null;
  submissionStateCounts: Record<string, number> | null;
  oldestUnacknowledgedSubmissionSeconds: number | null;
  lastAuthoritativeStatusAt: string | null;
  networkState: string | null;
  createdAt: string | null;
}

interface EvidenceOperationInventoryEntry {
  holding: boolean;
  durableServerReceipt: boolean;
  localFilePresent: boolean | null;
}

interface UnsafeCounts {
  pending: number;
  processing: number;
  failed: number;
  failed_permanent: number;
  conflict: number;
}

export interface ActionRequiredSubmission {
  operationId: string;
  storeId: number;
  cycleId: number;
  storeName?: string | null;
  cycleName?: string | null;
  state: string;
  lastErrorCode: string | null;
  lastErrorMessage: string | null;
  unconfirmedItems?: Array<{ eventType: string; count: number }>;
  expectedOperationCount: number;
  acknowledgedOperationCount: number;
  updatedAt: string;
}

export function formatVisitLabel(submission: ActionRequiredSubmission): string {
  const store = submission.storeName?.trim() || `Store ${submission.storeId}`;
  const cycle = submission.cycleName?.trim() || `Cycle ${submission.cycleId}`;
  return `${store} · ${cycle}`;
}

// These are phone-reported delivery stages, not compliance approval or proof of all photos.
export function describeReportedSubmissionState(state: string): string {
  const labels: Record<string, string> = {
    local_pending: 'Saved work waiting to send',
    upload_session_created: 'Photo upload prepared; delivery still outstanding',
    media_uploaded: 'Some photos received; job delivery still outstanding',
    proof_finalized: 'Some job records received; visit delivery not yet complete',
    acknowledged: 'Visit delivery confirmed; compliance approval is separate',
    retryable_failure: 'Sending delayed; app can retry',
    action_required: 'Saved work flagged for investigation',
    conflict: 'Saved work needs matching with server records',
  };
  return labels[state] ?? 'Delivery stage unavailable';
}

// Counts describe server-known work, not proof that a photo file exists.
export function describeUnconfirmedItems(submission: ActionRequiredSubmission): string[] {
  if (!submission.unconfirmedItems) return ['Item details are not available. Check with the operator before closing this issue.'];
  if (submission.unconfirmedItems.length === 0) return ['No individual items are listed as waiting. Check with the operator before closing this issue.'];
  const names: Record<string, [string, string]> = {
    PHOTO_UPLOAD: ['photo', 'photos'],
    STORE_SIGNOFF: ['manager signature', 'manager signatures'],
    TASK_COMPLETION: ['completed job', 'completed jobs'],
  };
  return submission.unconfirmedItems.map(item => {
    const [singular, plural] = names[item.eventType] ?? ['other item', 'other items'];
    return `${item.count} ${item.count === 1 ? singular : plural}`;
  });
}

interface SyncFlightRecorderSnapshot {
  id: string;
  reference: string;
  trigger: string;
  capturedAt: string;
  appVersion: string | null;
  buildNumber: string | null;
  runtimeVersion: string | null;
  otaUpdateId: string | null;
  networkState: string | null;
  payload: SyncFlightRecorderPayload;
  createdAt: string;
}

type HealthAssessment = {
  status: "green" | "amber" | "red" | "slate";
  label: string;
  reason: string;
};

export function computeStatus(snapshot: DeviceHealthSnapshot): HealthAssessment {
  const delivery = describeOperatorDelivery(snapshot);
  const status = delivery.tone === 'danger' ? 'red' : delivery.tone === 'warning' ? 'amber' : delivery.tone === 'good' ? 'green' : 'slate';
  return { status, label: delivery.label, reason: delivery.explanation };
}

function StatusBadge({ assessment }: { assessment: HealthAssessment }) {
  if (assessment.status === "green") {
    return (
      <Badge className="bg-green-100 text-green-800 border-green-200 flex items-center gap-1">
        <CheckCircle className="h-3 w-3" />
        {assessment.label}
      </Badge>
    );
  }
  if (assessment.status === "amber") {
    return (
      <Badge className="bg-amber-100 text-amber-800 border-amber-200 flex items-center gap-1">
        <AlertTriangle className="h-3 w-3" />
        {assessment.label}
      </Badge>
    );
  }
  if (assessment.status === "red") {
    return (
      <Badge className="bg-red-100 text-red-800 border-red-200 flex items-center gap-1">
        <AlertTriangle className="h-3 w-3" />
        {assessment.label}
      </Badge>
    );
  }
  return (
    <Badge className="bg-slate-100 text-slate-800 border-slate-200 flex items-center gap-1">
      <AlertTriangle className="h-3 w-3" />
      {assessment.label}
    </Badge>
  );
}

function formatOldestEvent(seconds: number | null): string {
  if (seconds === null) return "Nothing waiting in the report";
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  return `${(seconds / 3600).toFixed(1)}h`;
}

function formatQueuedCount(count: number | null): string {
  if (count == null) return "Not reported";
  if (count === 0) return "Nothing waiting in the report";
  if (count === 1) return "1 awaiting confirmation";
  return `${count} awaiting confirmation`;
}

function formatProtectedPhotoCount(count: number | null): string {
  if (count == null) return "Not reported";
  if (count === 0) return "None";
  if (count === 1) return "1 photo that can’t be matched to a store";
  return `${count} photos that can’t be matched to a store`;
}

function formatUnsafeCounts(counts: UnsafeCounts | null): string {
  if (!counts) return "Not reported";
  return [
    `waiting ${counts.pending}`,
    `sending ${counts.processing}`,
    `retrying ${counts.failed}`,
    `stuck ${counts.failed_permanent}`,
    `clash ${counts.conflict}`,
  ].join(" · ");
}

function formatOperatingSystem(snapshot: DeviceHealthSnapshot): string {
  const osName = snapshot.osName?.trim();
  const osVersion = snapshot.osVersion?.trim();
  if (osName && osVersion) return `${osName} ${osVersion}`;
  if (osName) return osName;
  if (osVersion) return osVersion;
  return "Not reported";
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between items-start gap-4 py-1.5 border-b border-gray-50 last:border-0">
      <span className="text-sm text-muted-foreground shrink-0">{label}</span>
      <span className="text-sm font-medium text-right break-all">{value ?? "—"}</span>
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mt-4 mb-1">
      {children}
    </p>
  );
}

interface OperatorDeviceHealthModalProps {
  operatorId: number | null;
  operatorName: string | null;
  isOpen: boolean;
  onClose: () => void;
  readOnlyForSupport?: boolean;
  cycleId?: number;
  storeId?: number;
}

export default function OperatorDeviceHealthModal({
  operatorId,
  operatorName,
  isOpen,
  onClose,
  readOnlyForSupport = false,
  cycleId,
  storeId,
}: OperatorDeviceHealthModalProps) {
  const [loading, setLoading] = useState(false);
  const [snapshot, setSnapshot] = useState<DeviceHealthSnapshot | null>(null);
  const [exists, setExists] = useState<boolean | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [actionRequired, setActionRequired] = useState<ActionRequiredSubmission[]>([]);
  const [flightRecorders, setFlightRecorders] = useState<SyncFlightRecorderSnapshot[]>([]);
  const [expandedReference, setExpandedReference] = useState<string | null>(null);
  const [resolving, setResolving] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen || operatorId === null) return;

    let cancelled = false;
    setLoading(true);
    setSnapshot(null);
    setExists(null);
    setError(null);
    setActionRequired([]);
    setFlightRecorders([]);
    setExpandedReference(null);

    Promise.all([
      fetch(`/api/admin/operators/${operatorId}/device-health`).then(async (res) => {
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          return res.json() as Promise<{ exists: boolean; snapshot?: DeviceHealthSnapshot }>;
        }),
      fetch(`/api/mobile/offline/submissions/admin/action-required?operatorId=${operatorId}`)
        .then(async (res) => res.ok
          ? res.json() as Promise<{ submissions: ActionRequiredSubmission[] }>
          : { submissions: [] }),
      fetch(`/api/admin/operators/${operatorId}/sync-flight-recorders?limit=10`)
        .then(async (res) => res.ok
          ? res.json() as Promise<{ snapshots: SyncFlightRecorderSnapshot[] }>
          : { snapshots: [] }),
    ])
      .then(([data, submissionsData, flightRecorderData]) => {
        if (cancelled) return;
        setExists(data.exists);
        setSnapshot(data.snapshot ?? null);
        setActionRequired(submissionsData.submissions ?? []);
        setFlightRecorders(flightRecorderData.snapshots ?? []);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("[OperatorDeviceHealthModal] fetch error:", err);
        setError("Failed to load this phone report.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, operatorId]);

  const assessment = snapshot ? computeStatus(snapshot) : null;
  const visibleSubmissions = actionRequired.filter(submission => !readOnlyForSupport || (submission.cycleId === cycleId && (storeId == null || submission.storeId === storeId) && submission.expectedOperationCount > submission.acknowledgedOperationCount));

  const resolveAction = async (
    submission: ActionRequiredSubmission,
    action: 'retry' | 'discard' | 'supersede',
  ) => {
    const work = `${formatVisitLabel(submission)}\nStill not confirmed by the office:\n${describeUnconfirmedItems(submission).map(item => `• ${item}`).join('\n')}`;
    const explanations = {
      retry: 'Allow another attempt to send the work that failed. The phone will need to connect. Why are you trying again?',
      supersede: 'Close this issue because newer work replaces it. Only continue if you have checked the replacement work. This does not create new tasks or a new signature. Unsent work on the phone may still need attention. Why are you closing it?',
      discard: 'Close this issue for all the items listed above. This does not send the missing items or delete the store visit. Unsent work on the phone may still need attention. Why are you closing it?',
    };
    const reason = window.prompt(`${work}\n\n${explanations[action]}`);
    if (!reason?.trim()) return;
    setResolving(submission.operationId);
    try {
      const response = await fetch(
        `/api/mobile/offline/submissions/admin/${submission.operationId}/resolve`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action, reason: reason.trim() }),
        },
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      setActionRequired(current => current.filter(item => item.operationId !== submission.operationId));
    } catch (resolveError) {
      console.error('[OperatorDeviceHealthModal] resolution error:', resolveError);
      setError('Could not save your choice. Please try again.');
    } finally {
      setResolving(null);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Smartphone className="h-4 w-4 text-muted-foreground" />
            {readOnlyForSupport ? "Technical phone log" : "This phone"}
          </DialogTitle>
          <DialogDescription className="sr-only">{readOnlyForSupport ? "Read-only technical phone information for support." : "Information from the latest phone report."}</DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="py-8 text-center text-sm text-muted-foreground">Loading…</div>
        )}

        {!loading && error && (
          <div className="py-6 text-center text-sm text-destructive">{error}</div>
        )}

        {!loading && !error && exists === false && flightRecorders.length === 0 && (
          <div className="py-8 text-center text-sm text-muted-foreground">
            No phone report yet.
          </div>
        )}

        {!loading && !error && (
          <div className="space-y-1">
            {exists === true && snapshot && assessment && (
              <>
            {/* Header row */}
            <div className="flex items-center justify-between mb-3">
              <p className="font-semibold text-sm truncate">{operatorName ?? "Operator"}</p>
              {!readOnlyForSupport && <StatusBadge assessment={assessment} />}
            </div>
            {!readOnlyForSupport && <p className="text-xs text-muted-foreground -mt-1 mb-2">{assessment.reason}</p>}
            <p className="text-xs text-muted-foreground mb-2">Phone report: {formatPhoneReportTime(snapshot.createdAt)} · {snapshot.createdAt && Number.isFinite(Date.parse(snapshot.createdAt)) ? formatDistanceToNow(new Date(snapshot.createdAt), { addSuffix: true }) : "Time unavailable"}. This report covers saved work across all cycles.</p>
            {readOnlyForSupport ? <p className="text-xs mb-2">For technical support. Phone counts cover all stores and cycles. This log does not change any work.</p> : <p className="text-xs mb-2">Next step: {describeOperatorDelivery(snapshot).action}</p>}

            {/* Sync Health */}
            <SectionLabel>Sending status</SectionLabel>
            <Row
              label="Last successful send"
              value={
                snapshot.lastSyncAt
                  ? formatDistanceToNow(new Date(snapshot.lastSyncAt), { addSuffix: true })
                  : "Not reported"
              }
            />
            <Row
              label="Phone can check what is waiting"
              value={snapshot.unsafeStatusReadOk == null
                ? "Older app — not reported"
                : snapshot.unsafeStatusReadOk ? "Yes" : "No"}
            />
            <Row label="Items awaiting confirmation" value={formatQueuedCount(snapshot.unsafeCount ?? snapshot.outboxCount)} />
            <Row label="Oldest item still waiting" value={formatOldestEvent(snapshot.oldestUnsafeSeconds ?? snapshot.oldestEventSeconds)} />
            <Row label="What is happening" value={formatUnsafeCounts(snapshot.unsafeCounts)} />
            <Row
              label="Oldest store visit not fully confirmed"
              value={formatOldestEvent(snapshot.oldestUnacknowledgedSubmissionSeconds)}
            />
            <Row
              label="Last time phone checked the server"
              value={snapshot.lastAuthoritativeStatusAt
                ? formatDistanceToNow(new Date(snapshot.lastAuthoritativeStatusAt), { addSuffix: true })
                : "Not reported"}
            />
            <Row
              label="Failed sends in a row"
              value={snapshot.consecutiveFailures == null ? "Not reported" : snapshot.consecutiveFailures}
            />
            <Row
              label="Network"
              value={
                snapshot.networkState ? (
                  <span className="flex items-center gap-1 justify-end">
                    {snapshot.networkState.toLowerCase().includes("offline") ? (
                      <WifiOff className="h-3 w-3 text-red-500" />
                    ) : snapshot.networkState.toLowerCase().includes("unknown") ? (
                      <AlertTriangle className="h-3 w-3 text-amber-500" />
                    ) : (
                      <Wifi className="h-3 w-3 text-green-500" />
                    )}
                    {snapshot.networkState}
                  </span>
                ) : null
              }
            />

            {/* Workflow */}
            <SectionLabel>What is waiting</SectionLabel>
            <Row
              label="Photos awaiting confirmation"
              value={formatQueuedCount(snapshot.unsafeByEventType?.PHOTO_UPLOAD?.unsafeCount ?? null)}
            />
            <Row
              label="Signatures awaiting confirmation"
              value={formatQueuedCount(snapshot.unsafeByEventType?.STORE_SIGNOFF?.unsafeCount ?? snapshot.pendingSignoffs)}
            />
            <Row
              label="Completed jobs awaiting confirmation"
              value={formatQueuedCount(snapshot.unsafeByEventType?.TASK_COMPLETION?.unsafeCount ?? snapshot.pendingTaskCompletions)}
            />
            <Row
              label="Photos that can’t be matched to a store"
              value={formatProtectedPhotoCount(snapshot.protectedEvidenceMissingStoreCount)}
            />
            {(snapshot.protectedEvidenceMissingStoreCount ?? 0) > 0 && (
              <Row
                label="Oldest unmatched photo"
                value={formatOldestEvent(snapshot.oldestProtectedEvidenceMissingStoreSeconds)}
              />
            )}
            <Row
              label="Photos waiting to be matched"
              value={snapshot.evidenceOperationInventory == null
                ? "Not reported"
                : snapshot.evidenceOperationInventory.filter(entry => entry.holding).length}
            />
            <Row
              label="Of those, confirmed stored at the office"
              value={snapshot.evidenceOperationInventory == null
                ? "Not reported"
                : snapshot.evidenceOperationInventory.filter(
                  entry => entry.holding && entry.durableServerReceipt,
                ).length}
            />
            <Row
              label="Spare photo files on the phone"
              value={snapshot.orphanPhotoFileCount ?? "Not reported"}
            />
            {snapshot.evidenceOperationInventoryTruncated && (
              <p className="text-xs text-amber-700">
                Only some photo details are shown. Open Sync Support Diagnostics for more information.
              </p>
            )}

            {visibleSubmissions.length > 0 && (
              <div className="pt-2 space-y-2">
                <SectionLabel>{readOnlyForSupport ? "Stored sending records for this cycle" : "Work needing office help"}</SectionLabel>
                {visibleSubmissions.map(submission => (
                  <div key={submission.operationId} className="rounded border p-2 text-xs space-y-2">
                    <div>
                      <p className="font-semibold">{formatVisitLabel(submission)}</p>
                      <p className="text-muted-foreground">
                        {submission.acknowledgedOperationCount} of {submission.expectedOperationCount} items confirmed
                      </p>
                      <p className="mt-1">Still not confirmed by the office:</p>
                      <ul className="list-disc pl-4">
                        {(readOnlyForSupport && !submission.unconfirmedItems?.length ? ['The record does not list the individual waiting items.'] : describeUnconfirmedItems(submission)).map((item, index) => <li key={index}>{item}</li>)}
                      </ul>
                      {!readOnlyForSupport && <p className="mt-1 text-muted-foreground">These buttons apply to all the work listed here.</p>}
                      {(submission.lastErrorMessage || submission.lastErrorCode) && (
                        <details className="text-muted-foreground">
                          <summary>Technical details for support</summary>
                          <p>{submission.lastErrorCode}{submission.lastErrorCode && submission.lastErrorMessage ? ': ' : ''}{submission.lastErrorMessage}</p>
                        </details>
                      )}
                    </div>
                    {!readOnlyForSupport && <div className="flex gap-1 flex-wrap">
                      {canOfficeResolveSubmission(submission.state as MobileSubmissionState, 'retry') && (
                        <Button size="sm" variant="outline" disabled={resolving === submission.operationId}
                          onClick={() => void resolveAction(submission, 'retry')}>Try sending again</Button>
                      )}
                      {canOfficeResolveSubmission(submission.state as MobileSubmissionState, 'supersede') && (
                        <Button size="sm" variant="outline" disabled={resolving === submission.operationId}
                          onClick={() => void resolveAction(submission, 'supersede')}>Replaced by newer work</Button>
                      )}
                      {canOfficeResolveSubmission(submission.state as MobileSubmissionState, 'discard') && (
                        <Button size="sm" variant="destructive" disabled={resolving === submission.operationId}
                          onClick={() => void resolveAction(submission, 'discard')}>Close this issue</Button>
                      )}
                    </div>}
                  </div>
                ))}
              </div>
            )}

            {/* Device */}
            <SectionLabel>Phone</SectionLabel>
            <Row label="Phone ID" value={snapshot.deviceId ?? "Not reported"} />
            <Row label="Install ID" value={snapshot.installationId ?? "Not reported"} />
            <Row label="OS" value={formatOperatingSystem(snapshot)} />
            <Row label="App version" value={snapshot.appVersion ?? "Not reported"} />

            <SectionLabel>Local storage</SectionLabel>
            {snapshot.storageTelemetryReadOk === false && (
              <p className="text-xs text-amber-700">Could not read phone storage.</p>
            )}
            <PhoneStorageBreakdown diagnostics={snapshot.storageDiagnostics} />
            <Row label="Database size" value={snapshot.dbSizeMb == null ? "Not reported" : `${snapshot.dbSizeMb} MB`} />
            <Row
              label="Proof photos on phone"
              value={snapshot.offlineMediaFileCount == null && snapshot.offlineMediaBytes == null
                ? "Not reported"
                : `${snapshot.offlineMediaFileCount ?? 0} files · ${snapshot.offlineMediaBytes == null ? "size unknown" : `${Math.round(snapshot.offlineMediaBytes / (1024 * 1024))} MB`}`}
            />
            <Row label="Sent items still in phone history" value={snapshot.outboxSuccessRowCount ?? "Not reported"} />
            <Row label="Stuck permanent send failures" value={snapshot.outboxFailedPermanentRowCount ?? "Not reported"} />
            <Row
              label="Last automatic cleanup"
              value={snapshot.lastLocalRetentionAt
                ? `${formatDistanceToNow(new Date(snapshot.lastLocalRetentionAt), { addSuffix: true })} · ${snapshot.lastRetentionMediaDeletedCount ?? 0} photos / ${snapshot.lastRetentionOutboxRowsDeletedCount ?? 0} sent rows`
                : "Not reported"}
            />

            {snapshot.createdAt && (() => {
              const ageMinutes = Math.floor((Date.now() - new Date(snapshot.createdAt).getTime()) / 60000);
              const mayBeStale = ageMinutes >= 20;
              return (
                <p
                  className={`text-xs pt-2 text-right ${mayBeStale ? "text-amber-800 font-medium" : "text-muted-foreground"}`}
                >
                  This report:{" "}
                  {formatDistanceToNow(new Date(snapshot.createdAt), { addSuffix: true })}
                  {mayBeStale && (
                    <>
                      {" "}
                      · Ask the operator to open the app if you need a fresh check-in
                    </>
                  )}
                </p>
              );
            })()}
              </>
            )}

            {flightRecorders.length > 0 && (
              <div className="pt-2 space-y-2">
                <SectionLabel>Support snapshot</SectionLabel>
                <p className="text-xs text-muted-foreground">
                  Phone reports for support. Photos, signatures and sign-in details are not included.
                </p>
                {flightRecorders.map(recorder => {
                  const expanded = expandedReference === recorder.reference;
                  const payload = recorder.payload;
                  return (
                    <div key={recorder.id} className="rounded-md border p-3 text-xs space-y-2">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="font-mono text-sm font-bold text-teal-700">{recorder.reference}</p>
                          <p className="text-muted-foreground">
                            {formatDistanceToNow(new Date(recorder.capturedAt), { addSuffix: true })}
                            {" · "}
                            {recorder.trigger.replaceAll("_", " ")}
                          </p>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setExpandedReference(expanded ? null : recorder.reference)}
                        >
                          {expanded ? "Hide timeline" : "View timeline"}
                        </Button>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        <div><span className="text-muted-foreground">Still waiting</span><p className="font-semibold">{payload.summary.unsafeCount}</p></div>
                        <div><span className="text-muted-foreground">Retrying</span><p className="font-semibold">{payload.summary.failed}</p></div>
                        <div><span className="text-muted-foreground">Conflicts</span><p className="font-semibold">{payload.summary.conflicts}</p></div>
                        <div><span className="text-muted-foreground">Network</span><p className="font-semibold">{recorder.networkState ?? payload.context.networkState}</p></div>
                      </div>
                      <p className="text-muted-foreground">
                        App {recorder.appVersion ?? "?"} ({recorder.buildNumber ?? "?"})
                        {" · "}runtime {recorder.runtimeVersion ?? "?"}
                        {" · "}OTA {recorder.otaUpdateId?.slice(0, 8) ?? "embedded"}
                      </p>
                      {expanded && (
                        <div className="space-y-3 border-t pt-3">
                          <div>
                            <p className="font-semibold mb-1">Still waiting</p>
                            {payload.outbox.length === 0 ? (
                              <p className="text-muted-foreground">No waiting items were included in this report.</p>
                            ) : payload.outbox.map(item => (
                              <div key={item.eventId} className="border-l-2 border-amber-400 pl-2 py-1">
                                <p className="font-medium">
                                  #{item.eventId} {item.eventType} · {item.status} · attempt {item.attempts}
                                </p>
                                <p className="text-muted-foreground">
                                  assignment {item.assignmentId ?? "—"} · store {item.storeId ?? "—"} · cycle {item.cycleId ?? "—"}
                                </p>
                                {item.evidenceHolding && (
                                  <p className="text-teal-700">
                                    Protected in holding · server receipt {item.serverReceiptState ?? "pending"}
                                  </p>
                                )}
                                {item.error && <p className="text-destructive">{item.error}</p>}
                              </div>
                            ))}
                          </div>
                          <div>
                            <p className="font-semibold mb-1">Visit delivery at report time</p>
                            {payload.submissions.length === 0 ? (
                              <p className="text-muted-foreground">No outstanding work was included in this report.</p>
                            ) : payload.submissions.map(item => (
                              <p key={item.operationId} className="py-0.5">
                                Store {item.storeId} · cycle {item.cycleId} · revision {item.revision} · <strong>{describeReportedSubmissionState(item.state)}</strong>
                              </p>
                            ))}
                          </div>
                          <div>
                            <p className="font-semibold mb-1">Recent sending activity</p>
                            {payload.events.slice(0, 20).map((event, index) => (
                              <div key={`${event.timestamp}-${index}`} className="flex gap-2 py-0.5">
                                <span className="shrink-0 text-muted-foreground">
                                  {new Date(event.timestamp).toLocaleTimeString()}
                                </span>
                                <span><strong>{event.type}</strong>{event.message ? ` — ${event.message}` : ""}</span>
                              </div>
                            ))}
                          </div>
                          {payload.collectionErrors.length > 0 && (
                            <div>
                              <p className="font-semibold text-destructive mb-1">Details the phone could not report</p>
                              {payload.collectionErrors.map((message, index) => <p key={index}>{message}</p>)}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
